<template>
  <div :id="`${source}_support_email_settings`" class="flex flex-col gap-6 proxima_nova">
    <div class="text-2xl font-semibold primary_color">Support</div>

    <div v-if="isLoading" class="flex justify-center py-8">
      <a-spin size="large" />
    </div>

    <div v-else class="flex flex-col gap-8">
      <div class="flex flex-col gap-1">
        <div class="flex items-center gap-3">
          <a-switch
            size="small"
            v-model:checked="settings.enabled"
            @change="handleSave"
            :loading="isSaving"
          />
          <span class="text_color text-xl font-semibold"
            >Conversation Updates</span
          >
        </div>
        <span class="text-sm font-normal secondary_color ml-10">
          Share live chat updates by automatically sending email when customers
          go offline
        </span>
      </div>

      <div v-if="settings.enabled" class="flex flex-col gap-1">
        <span class="text-sm font-normal secondary_color">
          Email to send from
        </span>
        <a-select
          v-model:value="settings.default_mailbox_id"
          placeholder="Select default mailbox"
          class="w-full max-w-md"
          @change="handleSave"
          :loading="isSaving"
          :disabled="availableDefaultMailboxes.length === 0"
        >
          <a-select-option
            v-for="mailbox in availableDefaultMailboxes"
            :key="mailbox.id"
            :value="mailbox.id"
          >
            <div class="flex items-center gap-2">
              <ThemeImage
                v-if="mailbox.vendor"
                :icon="getVendorIcon(mailbox.vendor)"
                class="size-4"
                alt="vendor_icon"
              />
              {{ mailbox.email }}
            </div>
          </a-select-option>
        </a-select>
      </div>

      <div class="flex justify-between mt-2">
        <div class="flex flex-col gap-1">
          <span class="text_color text-xl font-semibold"
            >Connect email support channel</span
          >
          <span class="text-sm font-normal secondary_color">
            Setup the required email address to send or receive conversation in
            your inbox
          </span>
        </div>
        <a-button
          class="primary_medium cursor-pointer"
          @click="showAddEmailModal"
          >Add Email</a-button
        >
      </div>

      <CustomTable
        v-if="forwardingEmailsTableData.length > 0"
        :headers="emailTableHeaders"
        :data="forwardingEmailsTableData"
        :show-actions-column="true"
        :actions="actionOptions"
        :custom-column-render="customColumnRender"
        prefixImageBorderRadius="0%"
        :key="tableKey"
      />

      <div
        v-else
        class="flex flex-col items-center justify-center gap-2 mt-10"
      >
        <ThemeImage
          :icon="imageNames.EMPTY_SCREEN_GREY"
          alt="empty_icon"
          class="size-20"
        />
        <span class="text-base secondary_color text_color"
          >No forwarding emails added yet!</span
        >
      </div>
    </div>
  </div>

  <CustomModal v-model:visible="isAddEmailModal" title="Add Email" width="32vw">
    <div class="flex flex-col gap-2">
      <span class="text-[13px] secondary_color text_color">Email to use for forwarding</span>
      <a-select
        v-model:value="selectedMailboxId"
        placeholder="Select email"
        class="w-full"
        :disabled="availableForwardingMailboxes.length === 0"
      >
        <a-select-option
          v-for="mailbox in availableForwardingMailboxes"
          :key="mailbox.id"
          :value="mailbox.id"
        >
          <div class="flex items-center gap-2">
            <ThemeImage
              v-if="mailbox.vendor"
              :icon="getVendorIcon(mailbox.vendor)"
              class="size-4"
              alt="vendor_icon"
            />
            {{ mailbox.email }}
          </div>
        </a-select-option>
      </a-select>
      <span
        v-if="availableForwardingMailboxes.length === 0"
        class="text-xs text-gray-500"
      >
        No available mailboxes to add
      </span>
    </div>
    <template #footer>
      <div class="flex flex-row items-center gap-2">
        <a-button
          class="secondary_medium cursor-pointer"
          @click="isAddEmailModal = false"
          >Cancel</a-button
        >
        <a-button
          class="primary_medium cursor-pointer"
          :loading="isSaving"
          @click="addEmail"
          :disabled="!selectedMailboxId"
          >Add Email</a-button
        >
      </div>
    </template>
  </CustomModal>

  <CustomModal
    v-model:visible="isRemoveEmailModal"
    title="Remove Email?"
    width="32vw"
  >
    <span class="text-sm text_color"
      >Are you sure you want to remove this email?</span
    >
    <template #footer>
      <div class="flex flex-row items-center gap-2">
        <a-button
          class="secondary_medium cursor-pointer"
          @click="isRemoveEmailModal = false"
          >Cancel</a-button
        >
        <a-button
          class="primary_medium cursor-pointer"
          :loading="isSaving"
          @click="removeEmail"
          >Remove Email</a-button
        >
      </div>
    </template>
  </CustomModal>
</template>

<script setup>
import { ref, computed, onMounted, h } from "vue";
import { message } from "ant-design-vue";
import { imageNames } from "@/src/constants/imageConstants.js";
import ConversationService from "@/src/api/ConversationService.js";
import DashboardService from "@/src/api/DashboardService.js";
import ThemeImage from "@/src/components/ThemeImage.vue";
import CustomTable from "@/src/components/custom_table_components/CustomTable.vue";
import CustomModal from "@/src/components/CustomModal.vue";
import MailBoxRow from "@/src/components/MailBoxRow.vue";
import { emailVendorIcons } from "@/src/constants/settingConstants";

const props = defineProps({
  source: {
    type: String,
    required: true,
  },
});

const settings = ref({
  enabled: false,
  default_mailbox_id: null,
  forwarding_mailbox_ids: [],
});

const sharedMailboxes = ref([]);
const isLoading = ref(false);
const isSaving = ref(false);
const isAddEmailModal = ref(false);
const isRemoveEmailModal = ref(false);
const selectedMailboxId = ref(null);
const mailboxIdToRemove = ref(null);
const tableKey = ref(0);

const emailTableHeaders = [
  {
    key: "email",
    label: "Email",
    width: 12,
  },
];

const availableDefaultMailboxes = computed(() => {
  return sharedMailboxes.value.filter(
    (m) => !settings.value.forwarding_mailbox_ids.includes(m.id)
  );
});

const availableForwardingMailboxes = computed(() => {
  return sharedMailboxes.value.filter(
    (m) =>
      m.id !== settings.value.default_mailbox_id &&
      !settings.value.forwarding_mailbox_ids.includes(m.id)
  );
});

const forwardingEmailsTableData = computed(() => {
  return settings.value.forwarding_mailbox_ids
    .map((id) => {
      const mailbox = sharedMailboxes.value.find((m) => m.id === id);
      if (!mailbox) return null;
      return {
        email: {
          text: mailbox.email,
          prefixImage: getVendorIcon(mailbox.vendor),
          vendor: mailbox.vendor,
          id: mailbox.id,
        },
      };
    })
    .filter(Boolean);
});

const actionOptions = [
  {
    label: "Remove",
    handler: (row) => removeEmailConfirm(row),
    icon: imageNames.DELETE_GREY,
  },
];

const customColumnRender = {
  email: MailBoxRow,
};

const getVendorIcon = (vendor) => {
  return emailVendorIcons[vendor];
};

const fetchSettings = async () => {
  try {
    const response = await ConversationService.email.getSupportEmailSettings();
    if (response.success) {
      settings.value = {
        enabled: response.settings.enabled,
        default_mailbox_id: response.settings.default_mailbox?.id || null,
        forwarding_mailbox_ids:
          response.settings.forwarding_mailboxes?.map((m) => m.id) || [],
      };
      tableKey.value += 1;
    }
  } catch (error) {
    console.error("Error fetching settings:", error);
    message.error("Failed to load support email settings");
  }
};

const fetchSharedMailboxes = async () => {
  try {
    const response = await DashboardService.fetchSharedMailboxes();
    if (response.success) {
      sharedMailboxes.value = (response.email_accounts || []).filter(
        (account) => account.status !== "DISCONNECTED"
      );
    }
  } catch (error) {
    console.error("Error fetching shared mailboxes:", error);
  }
};

const handleSave = async () => {
  try {
    isSaving.value = true;
    const response = await ConversationService.email.updateSupportEmailSettings({
      enabled: settings.value.enabled,
      default_mailbox_id: settings.value.default_mailbox_id,
      forwarding_mailbox_ids: settings.value.forwarding_mailbox_ids,
    });
    if (response.success) {
      message.success("Settings saved successfully");
    } else {
      message.error(response.error || "Failed to save settings");
    }
  } catch (error) {
    console.error("Error saving settings:", error);
    message.error("Failed to save settings");
  } finally {
    isSaving.value = false;
  }
};

const showAddEmailModal = () => {
  selectedMailboxId.value = null;
  isAddEmailModal.value = true;
};

const addEmail = async () => {
  if (!selectedMailboxId.value) {
    message.error("Please select an email");
    return;
  }

  try {
    isSaving.value = true;
    settings.value.forwarding_mailbox_ids.push(selectedMailboxId.value);
    await handleSave();
    await fetchSettings();
    isAddEmailModal.value = false;
    selectedMailboxId.value = null;
  } catch (error) {
    console.error("Error adding email:", error);
    message.error("Failed to add email");
    const index = settings.value.forwarding_mailbox_ids.indexOf(
      selectedMailboxId.value
    );
    if (index > -1) {
      settings.value.forwarding_mailbox_ids.splice(index, 1);
    }
  } finally {
    isSaving.value = false;
  }
};

const removeEmailConfirm = (row) => {
  mailboxIdToRemove.value = row.email.id;
  isRemoveEmailModal.value = true;
};

const removeEmail = async () => {
  try {
    isSaving.value = true;
    const index = settings.value.forwarding_mailbox_ids.indexOf(
      mailboxIdToRemove.value
    );
    if (index > -1) {
      settings.value.forwarding_mailbox_ids.splice(index, 1);
      await handleSave();
      await fetchSettings();
      isRemoveEmailModal.value = false;
      mailboxIdToRemove.value = null;
    }
  } catch (error) {
    console.error("Error removing email:", error);
    message.error("Failed to remove email");
  } finally {
    isSaving.value = false;
  }
};

onMounted(async () => {
  isLoading.value = true;
  try {
    await Promise.all([fetchSettings(), fetchSharedMailboxes()]);
  } finally {
    isLoading.value = false;
  }
});
</script>